import { Kaggle } from "@kaggle/sdk";
import {
  getCurrentAccelerator,
  getCurrentDataSourceReferences,
  getCurrentDockerImageTag,
  getCurrentInternetEnabled,
  getCurrentKernelId,
  getCurrentLanguageId,
  getCurrentTitle,
  getCurrentWorkerPoolName,
} from "../../kernels/redux/selectors/current";
import { AppState } from "../../kernels/redux/types";
import { KernelVersionType } from "../../kernels/types/KernelVersionType";

/**
 * ONLY MODIFY THIS FILE IF YOU'RE CERTAIN OF WHAT YOU'RE DOING.
 *
 * The majority of updates to a kernel
 * are done by executing the same API request with variations
 * in the parameters sent.
 *
 * For example, if you want to start a worker for an existing kernel,
 * you call `createKernel` and provide the kernel id, title and language.
 * If you'd like to add a dataset to a kernel, you call the same
 * API endpoint but this time provide all the previous parameters
 * as well as the dataset to be added. You'll find this pattern for
 * most kernel operations.
 *
 * Ideally, we'd have a specific endpoint for each operation with
 * a well defined interface so we know exactly what parameters are
 * required. We're moving in that direction but in the meantime,
 * we'll mitigate the situation with some hackery present in this module.
 *
 * The main objective of the code in this module, is to minimize
 * the areas of the frontend affected by our current API interface state.
 */

/** The necessary parameters to start a kernel run request. */
export type CreateRunRequest = Kaggle.Sdk.Kernels.Requests.CreateKernelRunRequest;

/** Builds CreateKernelRunRequest params from app state and overrides. */
export type RunRequestBuilder = (
  runParams: Partial<CreateRunRequest>
) => CreateRunRequest;

/** Default CreateKernelRunRequest parameters. */
const DEFAULT_RUN_PARAMS = {
  forkedFromKernelId: null,
  kernelVersionId: null,
  dockerImageVersionId: null,
  pinnedDockerImageVersionId: null,
  ipynbContents: null,
  diff: null,
  forkParentDiff: null,
  versionType: "interactive" as KernelVersionType,
  immediatelyCancelSession: false,
};

/**
 * Maps Redux state to CreateKernelRunRequest (command handler params).
 * @param state The Redux app state.
 */
function mapStateToRunRequest(state: AppState): CreateRunRequest {
  const kernelId = getCurrentKernelId(state);
  const languageId = getCurrentLanguageId(state);
  const title = getCurrentTitle(state);
  if (!kernelId) {
    throw new Error("KernelId is a required CreateKernelRunRequest field.");
  }
  if (!title) {
    throw new Error("Title is a required CreateKernelRunRequest field.");
  }
  if (!languageId) {
    throw new Error("LanguageId is a required CreateKernelRunRequest field.");
  }
  return {
    ...DEFAULT_RUN_PARAMS,
    kernelId,
    title,
    languageId,
    dockerImageTag: getCurrentDockerImageTag(state),
    workerPoolName: getCurrentWorkerPoolName(state),
    dataSources: getCurrentDataSourceReferences(state),
    compute: {
      accelerator: getCurrentAccelerator(state),
      internet: { isEnabled: getCurrentInternetEnabled(state) },
      constraints: null,
    },
    useGivenDataSourceVersionIds: false,
    userPlanOverride: null,
  };
}

/**
 * Utility function which uses the current app state to build
 * a valid createKernelRun request payload.
 *
 * This function works by using the app state as the starting point
 * to populate the fields in the request body. The caller can then
 * override any of the fields by passing them in the function returned by
 * buildKernelRequest.
 */
export const buildRunRequest = (
  state: AppState
): RunRequestBuilder => runParams => {
  const runRequest = mapStateToRunRequest(state);

  // Merge together compute in a bit safer way.
  const compute = !runParams.compute
    ? runRequest.compute
    : {
        ...runRequest.compute,
        ...runParams.compute,
      };

  return {
    ...runRequest,
    ...runParams,
    compute,
  };
};
